import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Sparkles, RefreshCw, Loader2 } from "lucide-react";

export const KEYWORD_EXTS = [".pdf", ".png", ".jpg", ".jpeg", ".webp", ".docx", ".doc"];

export function KeywordChips({ tags, max, testId }) {
  if (!tags?.length) return null;
  const shown = max ? tags.slice(0, max) : tags;
  return (
    <div data-testid={testId} className="flex flex-wrap gap-1">
      {shown.map((t) => (
        <span key={t} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#1E73C9]/10 text-[#1E73C9]">{t}</span>
      ))}
      {max && tags.length > max && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#15233B]/5 text-[#15233B]/55">+{tags.length - max}</span>}
    </div>
  );
}

function Group({ label, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#15233B]/45 mb-1">{label}</div>
      <KeywordChips tags={items} testId={`resume-kw-${label.toLowerCase()}`} />
    </div>
  );
}

export default function ResumeKeywords({ card, doc, writable, onCard }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const tried = useRef(false);
  const kw = card.resume_keywords;
  const canRead = KEYWORD_EXTS.includes(doc.ext);

  const run = async (force) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/cards/${card.id}/resume-keywords`, { doc_id: doc.id, force });
      onCard(data);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  useEffect(() => {
    if (tried.current || kw || !writable || !canRead) return;
    tried.current = true;
    run(false);
  }, [kw, writable, canRead]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="resume-keywords" className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 flex items-center gap-1"><Sparkles className="h-3 w-3 text-[#FFCB05]" /> AI keywords</div>
        {writable && canRead && (
          <button data-testid="resume-kw-reextract" type="button" disabled={busy} onClick={() => run(true)}
            className="inline-flex items-center gap-1 text-[10px] font-bold text-[#1E73C9] hover:underline disabled:opacity-40">
            <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Re-extract
          </button>
        )}
      </div>
      {busy && <div data-testid="resume-kw-loading" className="flex items-center gap-1.5 text-xs text-[#15233B]/55"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading resume…</div>}
      {err && <div data-testid="resume-kw-error" className="text-[11px] text-[#b91c1c]">{err}</div>}
      {!busy && kw && (
        <div className="space-y-2">
          {kw.years_experience && <div data-testid="resume-kw-years" className="text-xs font-bold text-[#15233B]">{kw.years_experience} experience</div>}
          <Group label="Certifications" items={kw.certifications} />
          <Group label="Skills" items={kw.skills} />
          <Group label="Languages" items={kw.languages} />
          <div className="text-[10px] text-[#15233B]/40">From {kw.doc_filename}</div>
        </div>
      )}
      {!busy && !kw && !err && <div className="text-[11px] italic text-[#15233B]/45">{canRead ? "No keywords yet." : "This file type can't be read by AI."}</div>}
    </div>
  );
}
